import * as yup from 'yup'
import { AutoAwesome, Public } from '@mui/icons-material'
import {
  Box,
  Button,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  TooltipContent,
  Typography
} from '@dtx-company/design-system/src'
import { CodeConfigurationField } from '@dtx-company/inter-app/src/types/flowcode'
import { DEFAULT_FLOWCODE_OPTIONS } from '@app/code/src/components/LoggedOutCodeGeneratorV2/LoggedOutCodeGenerator.constants'
import { FC, useEffect, useRef, useState } from 'react'
import { FcGeneratorOptions } from '@dtx-company/flowcode-generator-types/src'
import { Flowcode } from '@dtx-company/flowcode-generator-browser'
import { FormControlLabel, Radio } from '@mui/material'
import { RIM_TEXT_CIRCLE_MAX_LENGTH } from '../GeneratorSections/CodeDesign/FrameAndText/constants'
import { ScanDestination } from '@app/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/ScanDestination/ScanDestination'
import { ServerSideError, ServerSideErrorType, cloneErrorObject, logger } from '@dtx-company/logger'
import { StringSchema } from 'yup'
import { createFlowcodeFromURL } from '@app/code/src/utils/flowcode'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getFlowcodeAutogeneratedOptionsWithRimText } from '@app/code/src/components/LoggedOutCodeGeneratorV2/AutobuilderSection/getFlowcodeOptionsWithRimText'
import { getFrameOptions } from '@app/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/CodeDesign/FrameAndText/utils/getFrameOptions'
import { useForm } from 'react-hook-form'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { yupResolver } from '@hookform/resolvers/yup'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import useTranslation from 'next-translate/useTranslation'

interface AutobuilderForm {
  url: string
  topRimText?: string
  bottomRimText?: string
  isSameURLAsScanDestination?: boolean | null
}

export const autoBuilderValidation: yup.ObjectSchema<AutobuilderForm> = yup.object().shape({
  url: yup.string().required('Please enter a URL'),
  isSameURLAsScanDestination: yup.boolean().nullable(),
  topRimText: yup.string().notRequired() as StringSchema<string | undefined>,
  bottomRimText: yup.string().notRequired() as StringSchema<string | undefined>
})

export const AutobuilderSection: FC = () => {
  const { t } = useTranslation('logged-out-generator')
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<AutobuilderForm>({
    resolver: yupResolver(autoBuilderValidation),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      isSameURLAsScanDestination: true
    }
  })
  const { locgService } = useGlobalServices()
  const topRimText = watch('topRimText')
  const bottomRimText = watch('bottomRimText')
  const isSameURLAsScanDestination = watch('isSameURLAsScanDestination')
  const [generatedCodes, setGeneratedCodes] = useState<
    { ids: string[]; configuration: FcGeneratorOptions }[]
  >([])
  const [allowToSubmit, setAllowToSubmit] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const scanDestinationOptionsRef = useRef<HTMLDivElement | null>(null)
  const differentScanDestinationContainer = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isSameURLAsScanDestination) {
      differentScanDestinationContainer?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      })
    }
  }, [isSameURLAsScanDestination])

  useEffect(() => {
    //scroll has to happen after generatedCodes is updated.
    // Because the div size changes with and without code, scroll wouldn't work correctly if do it inside Submit function
    if (generatedCodes.length > 0) {
      scanDestinationOptionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      })
    }
  }, [generatedCodes])

  const onHandleSubmit = async (data: AutobuilderForm): Promise<void> => {
    try {
      fireAnalyticsEvent('LoggedOutGenerator_AutobuilderForm_GenerateDesigns_Clicked', { ...data })
      setAllowToSubmit(false)
      setError(undefined)
      if (data.isSameURLAsScanDestination) {
        locgService.send({
          type: 'CHANGE',
          key: CodeConfigurationField.SCAN_DESTINATION,
          value: data.url
        })
      }

      const res = await createFlowcodeFromURL({ url: data.url, count: 3 })

      const codesWithRimText = res.autoDesignsFromUrl.map(code => ({
        ...code,
        configuration: getFlowcodeAutogeneratedOptionsWithRimText(
          code.configuration,
          data.topRimText,
          data.bottomRimText
        )
      }))
      setGeneratedCodes([
        {
          ids: [],
          configuration:
            data.bottomRimText || data.topRimText
              ? getFrameOptions(topRimText, bottomRimText)
              : DEFAULT_FLOWCODE_OPTIONS
        },
        ...codesWithRimText
      ])
    } catch (e) {
      logger.logError(
        new ServerSideError({
          ...cloneErrorObject(e),
          name: 'AutobuilderSection LOCG error',
          type: ServerSideErrorType.ServerSideInternalError,
          endpoint: 'createFlowcodeFromURL'
        }),
        { technicalArea: 'studio', team: 'PLG' }
      )
      setError(
        t('LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.optionsAreNotAvailable')
      )
    } finally {
      fireAnalyticsEvent(
        'LoggedOutGenerator_AutobuilderForm_GenerateDesigns_Clicked_No_Design_Options_Error',
        { url: data.url }
      )
      // Allow to submit only 6 seconds after the previous submit
      setTimeout(() => {
        setAllowToSubmit(true)
      }, 6000)
    }
  }

  return (
    <form onSubmit={handleSubmit(onHandleSubmit)}>
      <Stack gap={2} sx={{ mt: 2, mb: 7 }}>
        <Typography variant="subtitleSmall">
          {t('LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.heading')}
        </Typography>
        <Typography variant="bodySmall" color="onSurface.secondary">
          {t('LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.description')}
        </Typography>
      </Stack>
      <Stack gap={6}>
        <TextField
          {...register('url')}
          size="medium"
          label={t('LoggedOutGenerator.ScanDestination.options.url.label')}
          hiddenLabel
          placeholder={t(
            'LoggedOutGenerator.ScanDestination.options.url.subcomponents.UrlInput.placeholder'
          )}
          startAdornment={<Public sx={{ color: 'onSurface.secondary' }} />}
          error={Boolean(errors?.url?.message || error)}
          helperText={errors?.url?.message || error}
          fullWidth
          onFocus={() => fireAnalyticsEvent('LoggedOutGenerator_AutobuilderInputURL_Focused')}
          onBlur={e => {
            fireAnalyticsEvent('LoggedOutGenerator_AutobuilderInputURL_Blurred', {
              text: e.target.value
            })
          }}
        />
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="bodySmall" color="onSurface.secondary">
            {t('LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.rimTextHeading')}
          </Typography>
          <Stack direction="row" spacing={4}>
            <Box sx={{ width: '100%' }}>
              <TextField
                {...register('topRimText')}
                label={t('LoggedOutGenerator.frameAndText.input.topTextLabel')}
                placeholder={t('LoggedOutGenerator.frameAndText.input.placeholder')}
                type="text"
                inputProps={{
                  maxLength: RIM_TEXT_CIRCLE_MAX_LENGTH
                }}
                onBlur={e => {
                  fireAnalyticsEvent('LoggedOutGenerator_FrameTextInput_Blurred', {
                    text: e.target.value,
                    pos: 'top'
                  })
                }}
                startAdornment={<TextFieldsIcon />}
                fullWidth
                helperText={`${topRimText?.length || 0}/${RIM_TEXT_CIRCLE_MAX_LENGTH}`}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <TextField
                {...register('bottomRimText')}
                label={t('LoggedOutGenerator.frameAndText.input.bottomTextLabel')}
                placeholder={t('LoggedOutGenerator.frameAndText.input.placeholder')}
                type="text"
                inputProps={{
                  maxLength: RIM_TEXT_CIRCLE_MAX_LENGTH
                }}
                onBlur={e => {
                  fireAnalyticsEvent('LoggedOutGenerator_FrameTextInput_Blurred', {
                    text: e.target.value,
                    pos: 'bottom'
                  })
                }}
                startAdornment={<TextFieldsIcon />}
                fullWidth
                helperText={`${bottomRimText?.length || 0}/${RIM_TEXT_CIRCLE_MAX_LENGTH}`}
              />
            </Box>
          </Stack>
        </Stack>
        <Button
          color="tertiary"
          size="large"
          startIcon={AutoAwesome}
          type="submit"
          disabled={!allowToSubmit}
        >
          {generatedCodes.length > 0
            ? t('LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.button')
            : t('LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.buttonInitial')}
        </Button>
        <Stack direction={'row'} sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
          {generatedCodes.length > 0 &&
            generatedCodes.map((code, index) => (
              <Box sx={{ width: '25%', aspectRatio: '1 / 1' }} key={code.ids[0] || index}>
                <Button
                  variant={'text'}
                  onClick={() =>
                    locgService.send({
                      type: 'CHANGE',
                      key: CodeConfigurationField.AUTOGENERATED_OPTIONS,
                      value: code.configuration
                    })
                  }
                >
                  <Flowcode alt={'code design option ' + index} options={code.configuration} />
                </Button>
              </Box>
            ))}
        </Stack>
        <Stack ref={scanDestinationOptionsRef} gap={4}>
          <Stack gap={2} direction={'row'}>
            <Typography variant={'subtitleSmall'}>
              {t(
                'LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.scanDestinationQuestion'
              )}
            </Typography>
            <Tooltip
              title={
                <TooltipContent
                  title={t(
                    'LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.scanDestinationTooltipTitle'
                  )}
                  content={t(
                    'LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.scanDestinationTooltipDescription'
                  )}
                />
              }
            >
              <Typography variant={'subtitleSmall'} component={'span'}>
                <HelpOutlineIcon
                  fontSize={'inherit'}
                  aria-hidden="false"
                  role="button"
                  tabIndex={0}
                  aria-label="scan destination tooltip"
                />
              </Typography>
            </Tooltip>
          </Stack>
          <RadioGroup
            label="Change the URL of the scan destination?"
            hiddenLabel
            row
            defaultValue={true}
            {...register('isSameURLAsScanDestination')}
          >
            <FormControlLabel
              label={
                <Typography variant={'subtitleSmall'}>
                  {t(
                    'LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.theSameURLLabel'
                  )}
                </Typography>
              }
              value={true}
              control={<Radio />}
            />

            <FormControlLabel
              label={
                <Typography variant={'subtitleSmall'}>
                  {t(
                    'LoggedOutGenerator.AutobuilderVariantGenerator.Tabs.Autobuilder.differentURLLabel'
                  )}
                </Typography>
              }
              value={false}
              control={<Radio />}
            />
          </RadioGroup>
        </Stack>
      </Stack>
      <Box
        ref={differentScanDestinationContainer}
        data-testid={'differentScanDestinationContainer'}
      >
        {!isSameURLAsScanDestination && <ScanDestination hideTitle />}
      </Box>
    </form>
  )
}
